import { Category, Content, MainCategory, SubCategory } from '@/lib/categories/categories'
import { parseKeywords } from '@/lib/utils'
import { AssetType, SearchPath } from '@/lib/assets/types'
import { CATEGORIES_BUNDLED, CATEGORIES_JSON_URL } from '@/lib/config'
import bundledCategories from '@/data/categories.json'
import { readCategoryEditorialFields } from './category-editorial-fields'

export type CategoriesData = {
  mainCategories: MainCategory[]
  categories: Category[]
  subCategories: SubCategory[]
  contents: Content[]
}

type CategoriesTree = typeof bundledCategories

// Cache for dynamic categories loading
let cachedData: CategoriesData | null = null
let cachePromise: Promise<CategoriesData> | null = null

const fetchRemoteCategories = async (): Promise<CategoriesTree> => {
  const res = await fetch(CATEGORIES_JSON_URL)
  if (!res.ok) {
    throw new Error(`Failed to fetch categories: ${res.statusText}`)
  }
  return res.json()
}

const transformCategories = (tree: CategoriesTree): CategoriesData => {
  return {
    mainCategories: tree.mainCategories.map((mainCategory: any) => ({
      id: mainCategory.id,
      name: mainCategory.name,
      slug: mainCategory.slug,
      image: mainCategory.image,
      searchPath: mainCategory.search_path as SearchPath,
      banner: mainCategory.banner,
      title: mainCategory.title || mainCategory.name,
      description: mainCategory.description,
      ...readCategoryEditorialFields(mainCategory),
      keywords: parseKeywords(mainCategory.keywords),

      // Include categories in mainCategory
      categories: mainCategory.categories?.map((category: any) => ({
        id: category.id,
        name: category.name,
        slug: category.slug,
        image: category.image,
        icon: category.icon,
        title: category.title || category.name,
        description: category.description,
        ...readCategoryEditorialFields(category),
        keywords: parseKeywords(category.keywords),
        assetType: category.asset_type as AssetType,
        mainCategoryId: mainCategory.id,
        mainCategorySlug: mainCategory.slug,
      })),
    })),
    categories: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.map((category: any) => ({
        id: category.id,
        name: category.name,
        slug: category.slug,
        image: mainCategory.image,
        icon: category.icon,
        title: category.title || category.name,
        keywords: parseKeywords(category.keywords),
        description: category.description,
        ...readCategoryEditorialFields(category),
        assetType: category.asset_type as AssetType,
        mainCategoryId: mainCategory.id,
        mainCategorySlug: mainCategory.slug,
      }))
    ),
    subCategories: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.flatMap((category: any) =>
        category.subcategories.map((subCategory: any) => ({
          id: subCategory.id,
          name: subCategory.name,
          slug: subCategory.slug,
          image: subCategory.image,
          title: subCategory.title || subCategory.name,
          description: subCategory.description,
          ...readCategoryEditorialFields(subCategory),
          keywords: parseKeywords(subCategory.keywords),
          mainCategoryId: mainCategory.id,
          categoryId: category.id,
          mainCategorySlug: mainCategory.slug,
          categorySlug: category.slug,
        }))
      )
    ),
    contents: tree.mainCategories.flatMap((mainCategory: any) =>
      mainCategory.categories?.flatMap((category: any) =>
        category.subcategories.flatMap((subCategory: any) =>
          subCategory.contents.map((content: any) => ({
            id: content.id,
            slug: content.slug,
            name: content.name,
            searchTag: 'search_tag' in content ? (content.search_tag as string) : undefined,
            searchQuery: 'search_query' in content ? (content.search_query as string) : undefined,
            searchBroadness: 'search_broadness' in content ? (content.search_broadness as number) : undefined,
            minSearchBroadness:
              'min_search_broadness' in content ? (content.min_search_broadness as number) : undefined,
            searchCategory: 'search_category' in content ? (content.search_category as string) : undefined,
            image: content.image,
            title: content.title || content.name,
            description: content.description,
            ...readCategoryEditorialFields(content),
            keywords: parseKeywords(content.keywords),
            mainCategoryId: mainCategory.id,
            categoryId: category.id,
            subCategoryId: subCategory.id,
            mainCategorySlug: mainCategory.slug,
            categorySlug: category.slug,
            subCategorySlug: subCategory.slug,
          }))
        )
      )
    ),
  }
}

export const getData = async (): Promise<CategoriesData> => {
  // Bundled mode (default): use static import
  if (CATEGORIES_BUNDLED) {
    return transformCategories(bundledCategories as CategoriesTree)
  }

  // Dynamic mode: fetch from remote with caching
  if (cachedData) {
    return cachedData
  }

  // Deduplicate concurrent requests
  if (cachePromise) {
    return cachePromise
  }

  cachePromise = fetchRemoteCategories()
    .then((tree) => {
      cachedData = transformCategories(tree)
      return cachedData
    })
    .catch((error) => {
      // Fallback to bundled categories on fetch failure
      console.error('[categories] Remote fetch failed, falling back to bundled:', error)
      cachedData = transformCategories(bundledCategories as CategoriesTree)
      return cachedData
    })
    .finally(() => {
      cachePromise = null
    })

  return cachePromise
}
